/**
 * ToolEditorModal — edit a TOOL row inline (promote, disable, delete).
 *
 * Extracted from AiUsageDashboard.tsx:959-1114. Two corrections applied on
 * extraction: the delete window.confirm becomes a rose ConfirmModal, and the
 * three native <select> controls (method, coverage, status) become
 * SearchableDropdown.
 */
import React, { useState } from 'react';
import ConfirmModal from '../../../components/credits/ConfirmModal';
import SearchableDropdown from '../../../components/SearchableDropdown';
import {
  deleteTool, updateTool,
  type ToolCatalogRow, type ToolEditableFields,
} from '../../../services/aiControlApi';

interface Props {
  tool: ToolCatalogRow;
  onClose: () => void;
  onSaved: () => Promise<void> | void;
}

const INPUT = 'w-full px-3 py-2 min-h-[44px] text-sm border border-stone-200 rounded';

const FormRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold block mb-1">{label}</span>
    {children}
  </label>
);

const ToolEditorModal: React.FC<Props> = ({ tool, onClose, onSaved }) => {
  const t = tool as ToolCatalogRow & Record<string, any>;
  const [form, setForm] = useState<ToolEditableFields>({
    name: t.name || t.id,
    description: t.description || '',
    inputSchema: t.inputSchema,
    method: String(t.method || 'GET').toUpperCase(),
    baseUrl: t.baseUrl || '',
    baseUrlEnv: t.baseUrlEnv || '',
    path: t.path || '',
    requiredFeature: t.requiredFeature || 'pos.agents.analytics',
    domain: t.domain || 'misc',
    coverage: (t.coverage || 'stub') as any,
    status: (t.status || 'active') as any,
  });
  const [schemaText, setSchemaText] = useState(() => {
    try { return JSON.stringify(t.inputSchema || {}, null, 2); } catch { return '{}'; }
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const handleSave = async () => {
    setErr(null);
    let parsedSchema: any = undefined;
    if (schemaText.trim()) {
      try { parsedSchema = JSON.parse(schemaText); }
      catch { setErr('inputSchema is not valid JSON'); return; }
    }
    setSaving(true);
    try {
      await updateTool(t.id, { ...form, inputSchema: parsedSchema });
      await onSaved();
    } catch (e: any) {
      setErr(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const runDelete = async () => {
    setConfirmDelete(false);
    setSaving(true);
    try {
      await deleteTool(t.id);
      await onSaved();
    } catch (e: any) {
      setErr(e?.message || 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-auto">
        <div className="px-5 py-4 border-b border-stone-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Edit tool</h3>
            <p className="text-xs text-gray-500 font-mono">{t.id}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="min-h-[44px] min-w-[44px] text-gray-400 hover:text-gray-600 text-xl leading-none"
          >
            ✕
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {err && (
            <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded px-3 py-2 text-xs">{err}</div>
          )}

          <FormRow label="Name">
            <input value={form.name || ''} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={INPUT} />
          </FormRow>
          <FormRow label="Description (shown to Claude)">
            <textarea
              value={form.description || ''}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 text-sm border border-stone-200 rounded font-mono"
            />
          </FormRow>
          <div className="grid grid-cols-2 gap-3">
            <FormRow label="Method">
              <SearchableDropdown
                options={['GET', 'POST', 'PUT', 'DELETE'].map((m) => ({ value: m, label: m }))}
                value={form.method || 'GET'}
                onChange={(v) => setForm((f) => ({ ...f, method: v || 'GET' }))}
                placeholder="Method"
                searchPlaceholder="Search methods…"
              />
            </FormRow>
            <FormRow label="Path">
              <input value={form.path || ''} onChange={(e) => setForm((f) => ({ ...f, path: e.target.value }))} className={`${INPUT} font-mono`} />
            </FormRow>
            <FormRow label="Base URL (direct — preferred)">
              <input value={form.baseUrl || ''} onChange={(e) => setForm((f) => ({ ...f, baseUrl: e.target.value }))} placeholder="https://stock.api.dev.rubytill.com" className={`${INPUT} font-mono`} />
            </FormRow>
            <FormRow label="Base URL env (fallback)">
              <input value={form.baseUrlEnv || ''} onChange={(e) => setForm((f) => ({ ...f, baseUrlEnv: e.target.value }))} placeholder="POS_REPORTING_API_URL" className={`${INPUT} font-mono`} />
            </FormRow>
            <FormRow label="Domain">
              <input value={form.domain || ''} onChange={(e) => setForm((f) => ({ ...f, domain: e.target.value }))} className={INPUT} />
            </FormRow>
            <FormRow label="Required feature">
              <input value={form.requiredFeature || ''} onChange={(e) => setForm((f) => ({ ...f, requiredFeature: e.target.value }))} className={`${INPUT} font-mono`} />
            </FormRow>
            <FormRow label="Coverage">
              <SearchableDropdown
                options={['stub', 'partial', 'full', 'unknown'].map((c) => ({ value: c, label: c }))}
                value={form.coverage || 'stub'}
                onChange={(v) => setForm((f) => ({ ...f, coverage: (v || 'stub') as any }))}
                placeholder="Coverage"
                searchPlaceholder="Search…"
              />
            </FormRow>
            <FormRow label="Status">
              <SearchableDropdown
                options={['active', 'disabled'].map((s) => ({ value: s, label: s }))}
                value={form.status || 'active'}
                onChange={(v) => setForm((f) => ({ ...f, status: (v || 'active') as any }))}
                placeholder="Status"
                searchPlaceholder="Search…"
              />
            </FormRow>
          </div>
          <FormRow label="Input schema (JSON)">
            <textarea
              value={schemaText}
              onChange={(e) => setSchemaText(e.target.value)}
              rows={8}
              className="w-full px-3 py-2 text-xs border border-stone-200 rounded font-mono"
            />
          </FormRow>
        </div>

        <div className="px-5 py-3 border-t border-stone-200 flex items-center justify-between gap-2 sticky bottom-0 bg-white">
          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            disabled={saving}
            className="min-h-[44px] px-2 text-xs text-rose-600 hover:text-rose-700 hover:underline font-medium disabled:opacity-50"
          >
            Delete tool
          </button>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} disabled={saving} className="px-3 py-1.5 min-h-[44px] text-xs border border-stone-200 rounded hover:bg-stone-50">
              Cancel
            </button>
            <button type="button" onClick={() => void handleSave()} disabled={saving} className="px-4 py-1.5 min-h-[44px] text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50">
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>

      <ConfirmModal
        open={confirmDelete}
        title="Delete tool?"
        tone="rose"
        confirmLabel="Delete tool"
        busy={saving}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => void runDelete()}
        message={`Delete tool "${t.id}"? This removes it from the agent registry immediately.`}
      />
    </div>
  );
};

export default ToolEditorModal;
